import styled, { css } from 'styled-components';

import { useContextMediaQuery } from '@core/context';
import { EColor, ETypographyVariant, WithThemeProps } from '@core/type';
import { getPaletteHandlers } from '@core/util';

import { Grid } from '../grid';
import { Box } from '../layout';
import { Typography } from '../typography';
import { StyledGridContainerProps, StyledGridProps } from './interface-progress-stepper';

const ONE_HUNDRED_PERCENT = 100;
const ROUNDING = 2;

const MOBILE_PERCENT_WIDTH_OF_TILE = 3;

const StyledGridContainer = styled(Grid)<StyledGridContainerProps>`
  ${({
    totalSteps,
    currentIndex,
    isComplete,
    isMobile,
    theme,
  }: StyledGridContainerProps & WithThemeProps) => {
    const { palette } = theme;

    const { getColor } = getPaletteHandlers(palette);
    const backgroundBarInitialColor = getColor({
      commonType: EColor.BLUE,
      intensity: EColor.R500,
    });

    const backgroundColor = getColor({
      commonType: EColor.GRAY,
      intensity: EColor.R100,
    });

    const backgroundBarFinalColor = getColor({ commonType: EColor.BLUE, intensity: EColor.A400 });

    const mobileWidth = `calc(100% - ${
      (totalSteps - currentIndex) * MOBILE_PERCENT_WIDTH_OF_TILE
    }% + 18px)`;
    const desktopWidth = isComplete
      ? '100%'
      : `calc(${(((currentIndex + 1) / totalSteps) * ONE_HUNDRED_PERCENT).toFixed(
          ROUNDING,
        )}% + 15px)`;

    return css`
      position: relative;
      background-color: ${backgroundColor};
      height: 40px;
      border-radius: 8px;
      display: flex;
      flex-direction: row;

      &::before {
        content: '';
        position: absolute;
        width: ${isMobile ? mobileWidth : desktopWidth};
        transition: width 2s;
        border-radius: 8px;
        height: 40px;
        background-image: ${`linear-gradient(45deg, ${backgroundBarInitialColor}, ${backgroundBarFinalColor})`};
        clip-path: ${!isComplete &&
        'polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%)'};
      }
    `;
  }}
`;

const StyledGrid = styled(Grid)<StyledGridProps>`
  ${(props: StyledGridProps & WithThemeProps) => {
    const {
      hasBefore,
      totalSteps,
      isCurrent,
      isMobile,
      theme: { palette },
    } = props;

    const { getColor } = getPaletteHandlers(palette);
    const backgroundArrow = getColor({ commonType: EColor.GRAY, intensity: EColor.A50 });

    return css`
      padding: ${isCurrent ? '0 8px 0 24px' : '0px 10px'};
      z-index: 2;
      max-width: ${isMobile &&
      (isCurrent
        ? `calc(100% - ${MOBILE_PERCENT_WIDTH_OF_TILE * totalSteps}%)`
        : `calc(3% - 7px)`)};
      width: ${!isMobile && `calc(${(ONE_HUNDRED_PERCENT / totalSteps).toFixed(ROUNDING)}% - 6px)`};
      position: relative;
      margin-left: ${hasBefore ? '6px' : '0px'};
      content: '';

      &:hover {
        cursor: pointer;
      }

      &:before {
        content: '';
        visibility: ${hasBefore ? 'visible' : 'collapse'};
        position: absolute;
        top: 0;
        left: -6px;
        width: 22px;
        height: 100%;
        z-index: 2;
        background-color: ${backgroundArrow};
        clip-path: polygon(6px 0, 100% 50%, 6px 100%, 0 100%, 16px 50%, 0 0);
      }
    `;
  }}
`;

type ProgressStepperProps = {
  steps: string[];
  currentIndex: number;
  handleClickStep?: (index: number) => void;
};

export const ProgressStepper = ({ steps, handleClickStep, currentIndex }: ProgressStepperProps) => {
  const { isMobile } = useContextMediaQuery();

  return (
    <StyledGridContainer
      isContainer
      columns={12}
      columnSpacing={8}
      totalSteps={steps.length}
      currentIndex={currentIndex}
      isComplete={currentIndex === steps.length - 1}
      isMobile={isMobile}
    >
      {steps.map((step, index) => (
        <StyledGrid
          key={step}
          hasBefore={index > 0}
          totalSteps={steps.length}
          isCurrent={index === currentIndex}
          isMobile={isMobile}
        >
          <Box onClick={() => handleClickStep?.(index)}>
            {(!isMobile || index === currentIndex) && (
              <Typography
                textAlign="center"
                lineHeight="40px"
                color={
                  index === currentIndex
                    ? { commonType: EColor.WHITE, intensity: EColor.R900 }
                    : EColor.DESTRUCTIVE
                }
                variant={index === currentIndex ? ETypographyVariant.H6 : undefined}
              >
                {step}
              </Typography>
            )}
          </Box>
        </StyledGrid>
      ))}
    </StyledGridContainer>
  );
};
